import { useLanguage } from '../i18n/LanguageContext';
import { useCallback, useRef, useState, useEffect } from 'react';
import {
  deleteUser,
  fetchUsers,
  sendTemporaryPassword,
  updateUserEmail,
} from '../api/users';
import { registerCouponToUser } from '../api/coupons';
import { fetchCreditDetail } from '../api/credits';
import Pagination from '../components/Pagination';
import CouponSerialInput from '../components/CouponSerialInput';
import { hashPassword } from '../utils/crypto';
import { can } from '../utils/permissions';
import './UsersPage.css';

const PAGE_SIZE = 50;
const USER_TX_PAGE_SIZE = 30;

const reasonLabel = {
  Coupon: '쿠폰',
  Event: '이벤트',
  Refund: '환불',
  PlayGame: '게임 이용',
  Purchase: '구매',
  Claim: '지급',
};

const getReasonBadgeClass = (reason, amount = 0) => {
  if (reason === 'Claim' && amount < 0) return 'spend';
  if (reason === 'Coupon') return 'earn';
  if (reason === 'Event' || reason === 'Claim') return 'claim';
  if (reason === 'Refund') return 'refund';
  if (amount < 0) return 'spend';
  return 'earn';
};

function UsersPage() {
  const { t } = useLanguage();
  const DELETE_CONFIRM_TEXT = '사용자 삭제';
  const canUpdateUserEmail = can('user.email.update');
  const canResetUserPassword = can('user.password.reset');
  const canDeleteUser = can('user.delete');
  const canRegisterCoupon = can('coupon.register');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailConfirmModalOpen, setEmailConfirmModalOpen] = useState(false);
  const [emailSavedModalOpen, setEmailSavedModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordSentModal, setPasswordSentModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteDoneModalOpen, setDeleteDoneModalOpen] = useState(false);
  const [couponRegisterModalOpen, setCouponRegisterModalOpen] = useState(false);
  const [couponRegisterDoneModalOpen, setCouponRegisterDoneModalOpen] = useState(false);
  const [couponSerial, setCouponSerial] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [userTxPage, setUserTxPage] = useState(1);
  const [userTxDateInput, setUserTxDateInput] = useState('');
  const [userTxDateFilter, setUserTxDateFilter] = useState('');
  const [userCreditDetailLoading, setUserCreditDetailLoading] = useState(false);
  const [userCreditDetailError, setUserCreditDetailError] = useState('');
  const userTxDateInputRef = useRef(null);

  const loadUsers = useCallback(async (page = currentPage) => {
    // console.log(`loadUsers:: page: ${page}`);

    try {
      setLoading(true);
      setError('');
      const offset = (page - 1) * PAGE_SIZE;
      const res = await fetchUsers({
        offset,
        limit: PAGE_SIZE,
        search: searchTerm.trim(),
      });
      setUsers(res.data.users ?? []);
      setTotalUsers(Number(res.data.total ?? 0));
    } catch (err) {
      console.error('failed to load users:', err);

      setError('사용자 목록을 불러오지 못했습니다.');
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  // 현재 페이지 기준으로 CS 사용자 목록 API를 로드합니다.
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredUsers = users;

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearchTerm(searchInput.trim());
    setCurrentPage(1);
  };

  // DB 스키마 기반 상태 계산 헬퍼
  const getUserStatus = (u) => {
    // console.log(`getUserStatus:: ${u}`);

    if (u.deletedAt != null) return 'deleted';
    if (u.blocked) return 'blocked'; // 정지(Blocked)된 사용자
    if (!u.verified) return 'unverified';

    return 'active';
  };

  // session valid 값 및 DB 타임아웃 보조판단 결합 
  const isUserOnline = (user) => {
    // console.log(`isUserOnline:: ${user}`);

    if (user.session && user.session.valid === true) return true;
    if (!user.lastSignIn) return false;
    const diffMin = (new Date() - new Date(user.lastSignIn)) / 60000;
    return diffMin < 10; 
  };

  const formatDate = (dateString, withTime = false) => {
    // console.log(`formatDate::`);

    if (!dateString) return '-';
    const d = new Date(dateString);
    const datePart = `${d.getFullYear()}. ${String(d.getMonth()+1).padStart(2, '0')}. ${String(d.getDate()).padStart(2, '0')}`;
    if (withTime) {
      return `${datePart} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
    }
    return datePart;
  };

  const formatLocalDateKey = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '';

    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const formatNumber = (value) => {
    if (value === undefined || value === null || value === '') return '-';
    const number = Number(value);
    return Number.isNaN(number) ? String(value) : number.toLocaleString();
  };

  const openUserDetail = async (user) => {
    setSelectedUser(user);
    setUserTxPage(1);
    setUserTxDateInput('');
    setUserTxDateFilter('');
    setUserCreditDetailError('');

    const creditWallet = user.wallets?.find((item) => item.type === 'Credit') || null;
    if (!creditWallet?.uuid) return;

    try {
      setUserCreditDetailLoading(true);
      const detail = await fetchCreditDetail(creditWallet.uuid, {
        username: user.username,
        email: user.email,
      });
      const detailWallet = detail.wallets?.[0];

      setSelectedUser((prev) => {
        if (!prev || prev.uuid !== user.uuid || !detailWallet) return prev;

        return {
          ...prev,
          wallets: (prev.wallets || []).map((wallet) => (
            wallet.uuid === detailWallet.uuid ? detailWallet : wallet
          )),
          transactions: detail.transactions || [],
        };
      });
    } catch (err) {
      console.error('failed to load user credit detail:', err);
      setUserCreditDetailError('Transaction 내역을 불러오지 못했습니다.');
    } finally {
      setUserCreditDetailLoading(false);
    }
  };

  const getUserTransactions = (creditWallet) => {
    const walletTransactions = creditWallet?.ledger?.transactions;
    if (Array.isArray(walletTransactions)) return walletTransactions;
    return Array.isArray(selectedUser?.transactions) ? selectedUser.transactions : [];
  };

  const getUserTxPageData = (transactions) => {
    const start = (userTxPage - 1) * USER_TX_PAGE_SIZE;
    return transactions.slice(start, start + USER_TX_PAGE_SIZE);
  };

  const getUserTxTotalPages = (transactions) => {
    return Math.ceil(transactions.length / USER_TX_PAGE_SIZE);
  };

  const getFilteredUserTransactions = (transactions) => {
    if (!userTxDateFilter) return transactions;
    return transactions.filter((tx) => formatLocalDateKey(tx.createdAt) === userTxDateFilter);
  };

  const handleUserTxDateChange = (event) => {
    const selectedDate = event.target.value;
    setUserTxDateInput(selectedDate);
    setUserTxDateFilter(selectedDate);
    setUserTxPage(1);
  };

  const handleUserTxDateReset = () => {
    setUserTxDateInput('');
    setUserTxDateFilter('');
    setUserTxPage(1);
  };

  const openUserTxDatePicker = () => {
    if (typeof userTxDateInputRef.current?.showPicker === 'function') {
      userTxDateInputRef.current.showPicker();
      return;
    }

    userTxDateInputRef.current?.focus();
  };

  const handleUserTxDateKeyDown = (event) => {
    if (event.key === 'Tab') return;
    event.preventDefault();

    if (event.key === 'Enter' || event.key === ' ') {
      openUserTxDatePicker();
    }
  };

  // 계정 상태 뱃지를 렌더링하는 헬퍼 함수 (인증 / 미인증 / 차단 / 삭제 단일 컬럼 표시)
  const renderStatusBadge = (status) => {
    // console.log(`renderStatusBadge`);

    switch(status) {
      case 'active':
        return <span className="status-badge active">{t("인증")}</span>;
      case 'unverified':
        return <span className="status-badge unverified">{t("미인증")}</span>;
      case 'deleted':
        return <span className="status-badge deleted">삭제</span>;
      case 'blocked':
        return <span className="status-badge blocked">{t("차단")}</span>;
      default:
        return null;
    }
  };

  const updateUserInState = (uuid, patch) => {
    // console.log(`updateUserInState::`);

    setUsers((prev) => prev.map((user) => (
      user.uuid === uuid ? { ...user, ...patch } : user
    )));
    setSelectedUser((prev) => (
      prev?.uuid === uuid ? { ...prev, ...patch } : prev
    ));
  };

  const getActionErrorMessage = (err, fallback) => (
    err?.response?.data?.message || err?.message || fallback
  );

  const getPaginationInfo = () => {
    // console.log(`getPaginationInfo`);

    if (searchTerm) {
      return `현재 페이지 검색 결과 ${filteredUsers.length.toLocaleString()}명 / 전체 ${totalUsers.toLocaleString()}명`;
    }

    if (totalUsers === 0) return '총 0명 표시';

    const start = (currentPage - 1) * PAGE_SIZE + 1;
    const end = Math.min(start + users.length - 1, totalUsers);
    return `총 ${totalUsers.toLocaleString()}명 중 ${start.toLocaleString()}-${end.toLocaleString()} 표시`;
  };

  const handleUpdateEmail = async () => {
    // console.log(`handleUpdateEmail::`);

    if (!selectedUser) return;
    if (!canUpdateUserEmail) return;

    try {
      setActionLoading(true);
      setActionError('');
      const hashedPassword = await hashPassword(emailPassword);
      const res = await updateUserEmail({
        uuid: selectedUser.uuid,
        email: newEmail,
        password: hashedPassword,
      });
      updateUserInState(selectedUser.uuid, { email: res.email });
      setEmailPassword('');
      setEmailConfirmModalOpen(false);
      setEmailSavedModalOpen(true);
    } catch (err) {
      console.error('failed to update user email:', err);
      setActionError(getActionErrorMessage(err, '이메일 변경에 실패했습니다.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendTemporaryPassword = async () => {
    // console.log(`handleSendTemporaryPassword::`);

    if (!selectedUser) return;
    if (!canResetUserPassword) return;

    try {
      setActionLoading(true);
      setActionError('');
      await sendTemporaryPassword({
        username: selectedUser.username,
        email: selectedUser.email,
      });
      setPasswordModalOpen(false);
      setPasswordSentModal(true);
    } catch (err) {
      console.error('failed to send password reset email:', err);
      setActionError(getActionErrorMessage(err, '비밀번호 재설정 메일 발송에 실패했습니다.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    console.log(`handleDeleteUser::`);

    if (!selectedUser) return;
    if (!canDeleteUser) return;
    if (deleteConfirmText.trim() !== DELETE_CONFIRM_TEXT) return;

    try {
      setActionLoading(true);
      setActionError('');
      await deleteUser(selectedUser.uuid);
      updateUserInState(selectedUser.uuid, { deletedAt: new Date().toISOString() });
      setDeleteConfirmText('');
      setDeleteDoneModalOpen(true);
    } catch (err) {
      console.error('failed to delete user:', err);
      setActionError(getActionErrorMessage(err, '사용자 삭제에 실패했습니다.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegisterCoupon = async () => {
    if (!canRegisterCoupon) return;
    if (!selectedUser || !couponSerial.trim()) return;

    try {
      setActionLoading(true);
      setActionError('');
      await registerCouponToUser({
        serialNumber: couponSerial.trim(),
        userUuid: selectedUser.uuid,
      });
      await loadUsers();
      await openUserDetail(selectedUser);
      setCouponRegisterModalOpen(false);
      setCouponRegisterDoneModalOpen(true);
      setCouponSerial('');
    } catch (err) {
      console.error('failed to register coupon to user:', err);
      setActionError(getActionErrorMessage(err, '쿠폰 등록에 실패했습니다.'));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="users-page">
      {/* 화면 상단(헤더) 영역: 타이틀과 검색창, 내보내기 버튼 */}
      <div className="page-header">
        <div className="page-title-group">
          <div>
            <h2 className="page-title">{t("사용자 관리")}</h2>
            <p className="page-subtitle">{t("전체 사용자를 조회하고 상태를 관리할 수 있습니다.")}</p>
          </div>
          <div className="users-summary-bar">
            <span>{t("전체 조회된 사용자 수")}</span>
            <strong>{totalUsers.toLocaleString()}</strong>
          </div>
        </div>
        <div className="header-actions">
          <form className="search-form" onSubmit={handleSearchSubmit}>
            <div className="search-box">
              <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                type="text"
                placeholder={t("이름, 아이디, 이메일 입력 후 Enter")}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <button className="btn-search" type="submit" disabled={loading}>
              {t("검색")}
            </button>
          </form>
        </div>
      </div>

      {/* 리스트 테이블 영역 */}
      <div className="table-card">
        <table className="users-table">
          <thead>
            <tr>
              <th>{t("유저네임")}</th>
              <th>{t("닉네임")}</th>
              <th>{t("이메일")}</th>
              <th>{t("계정 상태")}</th>
              <th>{t("가입일")}</th>
              <th>{t("인증일")}</th>
              <th>{t("최근 접속")}</th>
              <th>balance</th>
              <th>wallet uuid</th>
              <th style={{ textAlign: 'center' }}>{t("관리")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="10" style={{textAlign:'center', padding:'40px'}}>{t("데이터를 불러오는 중입니다...")}</td></tr>
            ) : error ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '60px 0', color: '#dc2626' }}>
                  {error}
                </td>
              </tr>
            ) : filteredUsers.length > 0 ? (
              filteredUsers.map(user => {
                const displayName = user.username || '-';
                const nickname = user.profile?.nickname || user.nickname || '-';
                const wallet = user.wallets?.find((item) => item.type === 'Credit') || user.wallets?.[0];
                const walletBalance = formatNumber(wallet?.balance);
                const walletUuid = wallet?.uuid || '-';

                return (
                  <tr 
                    key={user.uuid} 
                    className="user-row" 
                    onClick={() => openUserDetail(user)}
                  >
                    <td>
                      <div className="user-cell">
                        <div className="user-info-text">
                          <div className="user-name">
                            {displayName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="detail-text">{nickname}</td>
                    <td className="detail-text">{user.email}</td>
                    <td>{renderStatusBadge(getUserStatus(user))}</td>
                    <td className="detail-text">{formatDate(user.createdAt)}</td>
                    <td className="detail-text">{formatDate(user.verifiedAt)}</td>
                    <td className="detail-text">{formatDate(user.lastSignIn)}</td>
                    <td className="detail-text">{walletBalance}</td>
                    <td className="detail-text wallet-uuid-cell">{walletUuid}</td>
                  <td style={{ textAlign: 'center' }}>
                    {/* 행 클릭과 버튼 클릭 이벤트를 분리하기 위해 stopPropagation 처리 */}
                    <button className="action-btn" onClick={(e) => {
                      e.stopPropagation();
                      openUserDetail(user);
                    }}>
                      {t("상세 보기")}
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </button>
                  </td>
                </tr>
              );
              })
            ) : (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '60px 0', color: '#6b7280' }}>
                  "{searchTerm}" 에 대한 검색 결과가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        
        <Pagination
          currentPage={currentPage}
          total={totalUsers}
          pageSize={PAGE_SIZE}
          info={getPaginationInfo()}
          disabled={loading}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* ── Drawer Overlay ── */}
      {selectedUser && (
        <div className="drawer-overlay" onClick={() => setSelectedUser(null)}></div>
      )}

      {/* ── Drawer Panel ── */}
      <div className={`detail-drawer ${selectedUser ? 'open' : ''}`}>
        {selectedUser && (() => {
          const statusStr = getUserStatus(selectedUser);
          const isOnline = isUserOnline(selectedUser);
          const isDeletedUser = !!selectedUser.deletedAt;
          const isBlockedUser = selectedUser.blocked === true;
          const disableEmailAction = isDeletedUser || isBlockedUser || !canUpdateUserEmail;
          const disablePasswordAction = isDeletedUser || isBlockedUser || !canResetUserPassword;
          const disableDeleteAction = isDeletedUser || !canDeleteUser;
          const creditWallet = selectedUser.wallets?.find((item) => item.type === 'Credit') || null;
          const userTransactions = getUserTransactions(creditWallet);
          const filteredUserTransactions = getFilteredUserTransactions(userTransactions);
          const userTxTotalPages = getUserTxTotalPages(filteredUserTransactions);
          const relatedDevices = Array.isArray(selectedUser.devices) ? selectedUser.devices : [];

          return (
            <>
              <div className="user-related-panel">
                <div className="related-panel-inner">
                  <section className="related-section">
                    <div className="related-section-header">
                      <div>
                        <p className="related-section-eyebrow">Device</p>
                        <h4>{t("기기 목록")}</h4>
                      </div>
                    </div>
                    {relatedDevices.length > 0 ? (
                      <div className="related-table-wrap">
                        <table className="related-table">
                          <thead>
                            <tr>
                              <th>{t("모델")}</th>
                              <th>{t("시리얼 번호")}</th>
                              <th>{t("버전")}</th>
                              <th>{t("업데이트")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {relatedDevices.map((device) => (
                              <tr key={device.uuid || device.serialNumber}>
                                <td>{device.model || '-'}</td>
                                <td className="related-mono">{device.serialNumber || '-'}</td>
                                <td>{device.version || device.mmi || device.lm || '-'}</td>
                                <td>{formatDate(device.updatedAt)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="related-empty">{t("연결된 기기 정보가 없습니다.")}</div>
                    )}
                  </section>

                  <section className="related-section related-section-compact">
                    <div className="related-section-header">
                      <div className="credit-heading">
                        <div>
                          <p className="related-section-eyebrow">Credit</p>
                          <h4>{t("크레딧")}</h4>
                        </div>
                        <div className="credit-inline-balance">
                          <span>balance</span>
                          <strong>{formatNumber(creditWallet?.balance)}</strong>
                        </div>
                      </div>
                    </div>
                    <div className="credit-summary-grid">
                      <div className="credit-summary-item">
                        <label>wallet uuid</label>
                        <span className="related-mono">{creditWallet?.uuid || '-'}</span>
                      </div>
                    </div>
                    <div className="user-transaction-section">
                      <div className="user-transaction-header">
                        <div>
                          <p className="related-section-eyebrow">Transaction</p>
                          <h5>{t("거래내역")}</h5>
                        </div>
                        <div className="user-transaction-date-filter">
                          <div className="tx-date-picker" onClick={openUserTxDatePicker}>
                            <input
                              ref={userTxDateInputRef}
                              type="date"
                              aria-label="거래내역 날짜 선택"
                              inputMode="none"
                              value={userTxDateInput}
                              onChange={handleUserTxDateChange}
                              onKeyDown={handleUserTxDateKeyDown}
                              onPaste={(e) => e.preventDefault()}
                            />
                            <button
                              type="button"
                              className="tx-date-picker-btn"
                              aria-label="달력에서 거래내역 날짜 선택"
                              onClick={(e) => {
                                e.stopPropagation();
                                openUserTxDatePicker();
                              }}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <rect x="3" y="4.5" width="18" height="16" rx="2.5" stroke="currentColor" strokeWidth="2"/>
                                <path d="M8 2.5V6.5M16 2.5V6.5M3 9H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                            </button>
                          </div>
                          <button
                            type="button"
                            className="tx-filter-btn secondary"
                            disabled={!userTxDateInput && !userTxDateFilter}
                            onClick={handleUserTxDateReset}
                          >
                            {t("초기화")}
                          </button>
                        </div>
                        <strong>{filteredUserTransactions.length.toLocaleString()}건</strong>
                      </div>

                      {userCreditDetailLoading ? (
                        <div className="related-empty user-transaction-empty">{t("Transaction 내역을 불러오는 중입니다...")}</div>
                      ) : userCreditDetailError ? (
                        <div className="related-empty user-transaction-empty error">{userCreditDetailError}</div>
                      ) : userTransactions.length === 0 ? (
                        <div className="related-empty user-transaction-empty">{t("Transaction 내역이 없습니다.")}</div>
                      ) : filteredUserTransactions.length === 0 ? (
                        <div className="related-empty user-transaction-empty">{t("해당 날짜의 거래내역이 없습니다.")}</div>
                      ) : (
                        <>
                          <div className="user-transaction-table-wrap">
                            <table className="user-transaction-table">
                              <thead>
                                <tr>
                                  <th>Reason</th>
                                  <th>Amount</th>
                                  <th>Description</th>
                                  <th>{t("일시")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {getUserTxPageData(filteredUserTransactions).map((tx) => (
                                  <tr key={tx.uuid}>
                                    <td>
                                      <span className={`reason-badge ${getReasonBadgeClass(tx.reason, tx.amount)}`}>
                                        {tx.reason === 'Claim' ? t(tx.amount < 0 ? '회수' : '지급') : reasonLabel[tx.reason] ?? tx.reason ?? '-'}
                                      </span>
                                    </td>
                                    <td className={tx.amount > 0 ? 'amount-plus' : 'amount-minus'}>
                                      {tx.amount > 0 ? '+' : ''}{formatNumber(tx.amount)}
                                    </td>
                                    <td>{tx.description || '-'}</td>
                                    <td>{formatDate(tx.createdAt, true)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {userTxTotalPages > 1 && (
                            <div className="user-tx-pagination">
                              <button
                                className={`tx-page-btn ${userTxPage <= 1 ? 'disabled' : ''}`}
                                onClick={() => userTxPage > 1 && setUserTxPage(userTxPage - 1)}
                              >
                                {t("이전")}
                              </button>
                              {Array.from({ length: userTxTotalPages }, (_, i) => (
                                <button
                                  key={i + 1}
                                  className={`tx-page-btn ${userTxPage === i + 1 ? 'active' : ''}`}
                                  onClick={() => setUserTxPage(i + 1)}
                                >
                                  {i + 1}
                                </button>
                              ))}
                              <button
                                className={`tx-page-btn ${userTxPage >= userTxTotalPages ? 'disabled' : ''}`}
                                onClick={() => userTxPage < userTxTotalPages && setUserTxPage(userTxPage + 1)}
                              >
                                {t("다음")}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </section>
                </div>
              </div>
              <div className="drawer-detail-panel">
              <div className="drawer-header">
                <h3>{t("사용자 상세 정보")}</h3>
                <button className="drawer-close-btn" onClick={() => setSelectedUser(null)}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
              </div>
              
              <div className="drawer-content">
                {/* Profile Summary */}
                <div className="drawer-profile-card">
                  <div className="drawer-profile-info">
                    <div className="drawer-field-label">{t("유저네임")}</div>
                    <div className="drawer-name">
                      {selectedUser.username}
                      {isOnline && <span className="online-dot large" title={t("접속중")}></span>}
                      <span className="drawer-badge">{renderStatusBadge(statusStr)}</span>
                    </div>
                    <div className="drawer-field-label drawer-field-label-spaced">{t("이메일")}</div>
                    <div className="drawer-username">{selectedUser.email}</div>
                  </div>
                </div>

                {/* 기본 기입 정보 탭 */}
                <div className="drawer-section">
                  <h4>{t("기본 가입 정보")}</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>{t("가입일 (CreatedAt)")}</label>
                      <span>{formatDate(selectedUser.createdAt, true)}</span>
                    </div>
                    <div className="info-item">
                      <label>{t("인증일 (VerifiedAt)")}</label>
                      <span>{formatDate(selectedUser.verifiedAt, true)}</span>
                    </div>
                    <div className="info-item">
                      <label>{t("고유 식별자 (UUID)")}</label>
                      <span style={{fontSize: '11px', color: '#6b7280'}}>{selectedUser.uuid}</span>
                    </div>
                  </div>
                </div>

                {/* 보안, 세션, 환경 인증 탭 */}
                <div className="drawer-section">
                  <h4>{t("인증 및 보안 세션 환경")}</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>{t("인증 여부")}</label>
                      <span>{selectedUser.verified ? (
                        <span style={{display:'inline-flex', alignItems:'center', gap:'6px'}}>
                          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <circle cx="9" cy="9" r="9" fill="#34C759"/>
                            <path d="M5.5 9.2L7.8 11.5L12.5 6.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          {t("인증 완료")}
                        </span>
                      ) : (
                        <span className="verification-state unverified">
                          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                            <circle cx="9" cy="9" r="9" fill="#FBBF24"/>
                            <path d="M9 4.8V9.4" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                            <circle cx="9" cy="13" r="1.1" fill="#fff"/>
                          </svg>
                          {t("미인증")}
                        </span>
                      )}</span>
                    </div>
                    <div className="info-item">
                      <label>{t("차단 여부 (Blocked)")}</label>
                      <span>{selectedUser.blocked ? '차단됨' : '-'}</span>
                    </div>
                    <div className="info-item">
                      <label>계정 삭제</label>
                      <span>{selectedUser.deletedAt ? '삭제' : '-'}</span>
                    </div>
                    <div className="info-item">
                      <label>{t("가입 IP")}</label>
                      <span>{selectedUser.joinIp || '-'}</span>
                    </div>
                  </div>
                </div>


              <div className="coupon-register-block">
                <button
                  type="button"
                  className="coupon-register-btn"
                  disabled={!canRegisterCoupon}
                  title={!canRegisterCoupon ? '쿠폰 등록 권한이 없습니다.' : undefined}
                  onClick={() => {
                    if (!canRegisterCoupon) return;
                    setCouponSerial('');
                    setActionError('');
                    setCouponRegisterModalOpen(true);
                  }}
                >
                  {t("쿠폰 등록")}
                </button>
              </div>

              {/* Actions Section */}
              <div className="drawer-section action-section">
                <h4>{t("사용자 정보 수정 (관리자 기능)")}</h4>
                
                <div className={`admin-action-card ${disableEmailAction ? 'disabled' : ''}`}>
                  <div className="action-text">
                    <strong>{t("메일 정보 수정")}</strong>
                    <p>
                      {isDeletedUser
                        ? '삭제된 사용자는 이메일을 변경할 수 없습니다.'
                        : isBlockedUser
                          ? '차단된 사용자는 이메일을 변경할 수 없습니다.'
                          : !canUpdateUserEmail
                            ? '이메일 변경 권한이 없습니다.'
                          : `현재: ${selectedUser.email}`}
                    </p>
                  </div>
                  <button
                    className="btn-secondary"
                    disabled={disableEmailAction}
                    onClick={() => {
                      if (disableEmailAction) return;
                      setActionError('');
                      setNewEmail('');
                      setEmailPassword('');
                      setEmailModalOpen(true);
                    }}
                  >
                    {t("이메일 변경")}
                  </button>
                </div>

                <div className={`admin-action-card warning ${disablePasswordAction ? 'disabled' : ''}`}>
                  <div className="action-text">
                    <strong>{t("비밀번호 재설정 메일 발송")}</strong>
                    <p>
                      {isDeletedUser
                        ? '삭제된 사용자에게는 비밀번호 재설정 메일을 발송할 수 없습니다.'
                        : isBlockedUser
                          ? '차단된 사용자에게는 비밀번호 재설정 메일을 발송할 수 없습니다.'
                          : !canResetUserPassword
                            ? '비밀번호 재설정 메일 발송 권한이 없습니다.'
                          : '가입된 이메일로 비밀번호 재설정 메일을 전송합니다.'}
                    </p>
                  </div>
                  <button
                    className="btn-warning"
                    disabled={disablePasswordAction}
                    onClick={() => {
                      if (disablePasswordAction) return;
                      setActionError('');
                      setPasswordModalOpen(true);
                    }}
                  >
                    {t("재설정 메일 발송")}
                  </button>
                </div>

                <div className={`admin-action-card danger ${disableDeleteAction ? 'disabled' : ''}`}>
                  <div className="action-text">
                    <strong>사용자 삭제</strong>
                    <p>
                      {isDeletedUser
                        ? '이미 삭제된 사용자입니다.'
                        : !canDeleteUser
                          ? '사용자 삭제 권한이 없습니다.'
                          : '해당 사용자 계정을 영구적으로 삭제합니다.'}
                    </p>
                  </div>
                  <button
                    className="btn-danger"
                    disabled={disableDeleteAction}
                    onClick={() => {
                      if (disableDeleteAction) return;
                      setActionError('');
                      setDeleteConfirmText('');
                      setDeleteModalOpen(true);
                    }}
                  >
                    사용자 삭제
                  </button>
                </div>

              </div>
            </div>
            </div>
            </>
          );
        })()}
      </div>

      {/* ── 쿠폰 등록 모달 ── */}
      {couponRegisterModalOpen && selectedUser && (
        <>
          <div
            className="modal-overlay"
            onClick={() => {
              if (actionLoading) return;
              setActionError('');
              setCouponRegisterModalOpen(false);
            }}
          ></div>
          <div className="modal">
            <div className="modal-header">
              <h3>{t("쿠폰 등록")}</h3>
              <button
                className="drawer-close-btn"
                disabled={actionLoading}
                onClick={() => {
                  setActionError('');
                  setCouponRegisterModalOpen(false);
                }}
              >
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-info">
                <label>{t("대상 사용자")}</label>
                <span>{selectedUser.username}</span>
              </div>
              <div className="modal-info">
                <label>{t("사용자 UUID")}</label>
                <span>{selectedUser.uuid}</span>
              </div>
              <div className="modal-field">
                <label>{t("쿠폰 시리얼넘버")}</label>
                <CouponSerialInput
                  placeholder="X4H5-ED2G-A39S-EE6Q"
                  value={couponSerial}
                  onValueChange={setCouponSerial}
                  disabled={actionLoading}
                />
              </div>
              <p className="modal-warning">
                해당 쿠폰이 이 사용자의 Wallet에 등록됩니다. 쿠폰 시리얼넘버와 대상 사용자를 정확히 확인하세요.
              </p>
              {actionError && <p className="modal-error">{actionError}</p>}
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                disabled={actionLoading}
                onClick={() => {
                  setActionError('');
                  setCouponRegisterModalOpen(false);
                }}
              >
                {t("취소")}
              </button>
              <button
                className="btn-primary"
                disabled={actionLoading || !couponSerial.trim()}
                onClick={handleRegisterCoupon}
              >
                {actionLoading ? t("등록 중...") : t("등록")}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── 쿠폰 등록 완료 모달 ── */}
      {couponRegisterDoneModalOpen && selectedUser && (
        <>
          <div className="modal-overlay modal-overlay-confirm" onClick={() => setCouponRegisterDoneModalOpen(false)}></div>
          <div className="modal modal-confirm">
            <div className="modal-header">
              <h3>{t("쿠폰 등록 완료")}</h3>
              <button className="drawer-close-btn" onClick={() => setCouponRegisterDoneModalOpen(false)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-success">
                {selectedUser.username} 사용자에게 쿠폰이 등록되었습니다.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-primary" onClick={() => setCouponRegisterDoneModalOpen(false)}>
                {t("확인")}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── 이메일 변경 모달 ── */}
      {emailModalOpen && selectedUser && (
        <>
          <div className="modal-overlay" onClick={() => { setEmailSavedModalOpen(false); setEmailConfirmModalOpen(false); setEmailModalOpen(false); setEmailPassword(''); }}></div>
          <div className="modal">
            <div className="modal-header">
              <h3>{t("이메일 변경")}</h3>
              <button className="drawer-close-btn" onClick={() => { setEmailSavedModalOpen(false); setEmailConfirmModalOpen(false); setEmailModalOpen(false); setEmailPassword(''); }}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-info">
                <label>{t("대상 사용자")}</label>
                <span>{selectedUser.username}</span>
              </div>
              <div className="modal-info">
                <label>{t("현재 이메일")}</label>
                <span>{selectedUser.email}</span>
              </div>
              <div className="modal-field">
                <label>{t("변경할 이메일")}</label>
                <input
                  type="email"
                  placeholder={t("새 이메일 주소를 입력하세요")}
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
              </div>
              <div className="modal-field">
                <label>{t("현재 비밀번호")}</label>
                <input
                  type="password"
                  placeholder={t("이메일 변경에 필요한 현재 비밀번호")}
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setEmailSavedModalOpen(false); setEmailConfirmModalOpen(false); setEmailModalOpen(false); setEmailPassword(''); }}>{t("취소")}</button>
              <button
                className="btn-primary"
                disabled={!newEmail || newEmail === selectedUser.email || !emailPassword}
                onClick={() => setEmailConfirmModalOpen(true)}
              >
                {t("변경 저장")}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── 이메일 변경 저장 확인 모달 ── */}
      {emailConfirmModalOpen && selectedUser && (
        <>
          <div className="modal-overlay modal-overlay-confirm" onClick={() => setEmailConfirmModalOpen(false)}></div>
          <div className="modal modal-confirm">
            <div className="modal-header">
              <h3>{t("변경 저장 확인")}</h3>
              <button className="drawer-close-btn" onClick={() => setEmailConfirmModalOpen(false)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-info">
                <label>{t("대상 사용자")}</label>
                <span>{selectedUser.username}</span>
              </div>
              <div className="modal-info">
                <label>{t("현재 이메일")}</label>
                <span>{selectedUser.email}</span>
              </div>
              <div className="modal-info">
                <label>{t("변경할 이메일")}</label>
                <span>{newEmail}</span>
              </div>
              <p className="modal-warning">
                {t("위 이메일 주소로 변경 내용을 저장하시겠습니까?")}
              </p>
              {actionError && <p className="modal-error">{actionError}</p>}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" disabled={actionLoading} onClick={() => setEmailConfirmModalOpen(false)}>{t("취소")}</button>
              <button className="btn-primary" disabled={actionLoading} onClick={handleUpdateEmail}>
                {actionLoading ? t("저장 중...") : t("저장")}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── 이메일 변경 저장 완료 모달 ── */}
      {emailSavedModalOpen && selectedUser && (
        <>
          <div className="modal-overlay modal-overlay-confirm" onClick={() => { setEmailSavedModalOpen(false); setEmailModalOpen(false); }}></div>
          <div className="modal modal-confirm">
            <div className="modal-header">
              <h3>{t("저장 완료")}</h3>
              <button className="drawer-close-btn" onClick={() => { setEmailSavedModalOpen(false); setEmailModalOpen(false); }}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-success">
                {t("이메일 변경 내용이 저장되었습니다.")}
              </p>
              <div className="modal-info">
                <label>{t("대상 사용자")}</label>
                <span>{selectedUser.username}</span>
              </div>
              <div className="modal-info">
                <label>{t("변경된 이메일")}</label>
                <span>{selectedUser.email}</span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-primary" onClick={() => { setEmailSavedModalOpen(false); setEmailModalOpen(false); }}>
                {t("확인")}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── 비밀번호 재설정 메일 발송 확인 모달 ── */}
      {passwordModalOpen && selectedUser && (
        <>
          <div className="modal-overlay" onClick={() => setPasswordModalOpen(false)}></div>
          <div className="modal">
            <div className="modal-header">
              <h3>{t("비밀번호 재설정 메일 발송")}</h3>
              <button className="drawer-close-btn" onClick={() => setPasswordModalOpen(false)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-info">
                <label>{t("대상 사용자")}</label>
                <span>{selectedUser.username}</span>
              </div>
              <div className="modal-info">
                <label>{t("발송 이메일")}</label>
                <span>{selectedUser.email}</span>
              </div>
              <p className="modal-warning">
                {t("해당 사용자에게 비밀번호 재설정 메일이 발송됩니다.")}
                <br />
                {t("계속하시겠습니까?")}
              </p>
              {actionError && <p className="modal-error">{actionError}</p>}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" disabled={actionLoading} onClick={() => setPasswordModalOpen(false)}>{t("취소")}</button>
              <button className="btn-danger" disabled={actionLoading} onClick={handleSendTemporaryPassword}>
                {actionLoading ? t("발송 중...") : t("발송")}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── 비밀번호 재설정 메일 발송 완료 모달 ── */}
      {passwordSentModal && selectedUser && (
        <>
          <div className="modal-overlay" onClick={() => setPasswordSentModal(false)}></div>
          <div className="modal">
            <div className="modal-header">
              <h3>{t("발송 완료")}</h3>
              <button className="drawer-close-btn" onClick={() => setPasswordSentModal(false)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-success">{selectedUser.email} 으로 비밀번호 재설정 메일이 발송되었습니다.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-primary" onClick={() => setPasswordSentModal(false)}>{t("확인")}</button>
            </div>
          </div>
        </>
      )}

      {/* ── 사용자 삭제 확인 모달 ── */}
      {deleteModalOpen && selectedUser && (
        <>
          <div className="modal-overlay" onClick={() => { setDeleteDoneModalOpen(false); setDeleteModalOpen(false); setDeleteConfirmText(''); }}></div>
          <div className="modal">
            <div className="modal-header">
              <h3>사용자 삭제 확인</h3>
              <button className="drawer-close-btn" onClick={() => { setDeleteDoneModalOpen(false); setDeleteModalOpen(false); setDeleteConfirmText(''); }}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-info">
                <label>대상 사용자</label>
                <span>{selectedUser.username}</span>
              </div>
              <div className="modal-info">
                <label>이메일</label>
                <span>{selectedUser.email}</span>
              </div>
              <p className="modal-danger-warning">
                삭제 후에는 복구가 불가능합니다.
                <br />
                계속하려면 아래 입력칸에 확인 문구를 입력해주세요.
              </p>
              <div className="modal-field modal-field-danger">
                <label>확인 문구 - "사용자 삭제" 를 입력하세요</label>
                <input
                  type="text"
                  placeholder={DELETE_CONFIRM_TEXT}
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  disabled={actionLoading}
                />
              </div>
              {actionError && <p className="modal-error">{actionError}</p>}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" disabled={actionLoading} onClick={() => { setDeleteDoneModalOpen(false); setDeleteModalOpen(false); setDeleteConfirmText(''); }}>취소</button>
              <button className="btn-danger" disabled={actionLoading || deleteConfirmText.trim() !== DELETE_CONFIRM_TEXT} onClick={handleDeleteUser}>
                {actionLoading ? '삭제 중...' : '삭제'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── 사용자 삭제 완료 모달 ── */}
      {deleteDoneModalOpen && selectedUser && (
        <>
          <div className="modal-overlay modal-overlay-confirm" onClick={() => { setDeleteDoneModalOpen(false); setDeleteModalOpen(false); }}></div>
          <div className="modal modal-confirm">
            <div className="modal-header">
              <h3>삭제 완료</h3>
              <button className="drawer-close-btn" onClick={() => { setDeleteDoneModalOpen(false); setDeleteModalOpen(false); }}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-success">
                사용자 계정이 삭제되었습니다.
              </p>
              <div className="modal-info">
                <label>대상 사용자</label>
                <span>{selectedUser.username}</span>
              </div>
              <div className="modal-info">
                <label>이메일</label>
                <span>{selectedUser.email}</span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-primary" onClick={() => { setDeleteDoneModalOpen(false); setDeleteModalOpen(false); }}>
                확인
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default UsersPage;
